import React, { useContext, useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import Tracker from '@openreplay/tracker';
import { AuthContext } from './context/AuthContext';
import { supabase } from './supabaseClient';
import pdfDataCache from './components/documents/pdfDataCache';
import ErrorBoundary from './components/ErrorBoundary';

// Eager load kritieke componenten (login flow)
import Login from './pages/Login.jsx';
import RequireAuth from './components/auth/RequireAuth';
import Navbar from './components/NavBar.jsx';

// Lazy load alle overige pagina's voor betere initial load performance
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Projects = lazy(() => import('./pages/Projects.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const AdminPanel = lazy(() => import('./pages/AdminPanel.jsx'));
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ProjectManagement = lazy(() => import('./pages/ProjectManagement'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const CreateProject = lazy(() => import('./pages/CreateProject'));
const Library = lazy(() => import('./pages/Library.jsx'));
const StofSuggesties = lazy(() => import('./pages/StofSuggesties.jsx'));
const Metrics = lazy(() => import('./pages/Metrics.jsx'));
const Stoffenpakketten = lazy(() => import('./pages/Stoffenpakketten.jsx'));

// Suspense fallback component
const PageLoader = () => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '50vh',
    gap: 2
  }}>
    <CircularProgress size={40} />
    <Typography variant="body2" sx={{
      color: "text.secondary"
    }}>
      Laden...
    </Typography>
  </Box>
);

// OpenReplay tracker met privacy-first configuratie
// Wrapped in try-catch: a tracker crash must never take down the whole app
let tracker = null;
try {
  tracker = new Tracker({
    projectKey: import.meta.env.VITE_OPENREPLAY_KEY,
    capturePerformance: true,
    obscureTextEmails: true,
    obscureTextNumbers: true,
    obscureInputEmails: true,
    obscureInputNumbers: true,
    obscureInputDates: true,
    defaultInputMode: 1,
    network: {
      capturePayload: false,
      ignoreHeaders: ['authorization', 'cookie', 'x-api-key'],
      sanitizer: (data) => {
        if (data.url && data.url.includes('supabase')) {
          data.url = data.url.replace(/apikey=[^&]+/, 'apikey=[REDACTED]');
        }
        return data;
      }
    },
    consoleMethods: ['error', 'warn'],
  });

  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_TRACKING === 'true') {
    tracker.start();
  }
} catch (e) {
  console.warn('[OpenReplay] Tracker kon niet worden gestart:', e);
}

function App() {
  const location = useLocation();
  const { user, loading, role } = useContext(AuthContext);
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false);

  // Memory pressure monitoring en cache cleanup
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      pdfDataCache.cleanup();
    }, 60000);

    // Memory pressure detection (Chrome/Edge)
    if ('memory' in performance && 'onmemorywarning' in performance) {
      const handleMemoryWarning = () => {
        console.warn('Memory warning detected, clearing cache');
        pdfDataCache.handleMemoryPressure(75);
      };
      
      performance.addEventListener('memorywarning', handleMemoryWarning);
      
      return () => {
        clearInterval(cleanupInterval);
        performance.removeEventListener('memorywarning', handleMemoryWarning);
      };
    }

    return () => clearInterval(cleanupInterval);
  }, []);

  // Check of gebruiker volledig is geauthenticeerd (voor navbar)
  useEffect(() => {
    let isMounted = true;

    const checkFullAuth = async () => {
      if (!user) {
        if (isMounted) setIsFullyAuthenticated(false);
        return;
      }

      try {
        const [aalResult, factorsResult] = await Promise.all([
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          supabase.auth.mfa.listFactors()
        ]);

        if (!isMounted) return;

        const aalData = aalResult.data;
        const factorsData = factorsResult.data;
        
        const allFactors = [...(factorsData?.totp || []), ...(factorsData?.phone || [])];
        const hasVerifiedFactor = allFactors.some(factor => factor.status === 'verified');
        
        if (aalData?.currentLevel === 'aal2') {
          setIsFullyAuthenticated(true);
        } else if (!hasVerifiedFactor && user) {
          setIsFullyAuthenticated(true);
        } else {
          setIsFullyAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (isMounted) setIsFullyAuthenticated(false);
      }
    };

    checkFullAuth();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Identify user in OpenReplay when logged in
  useEffect(() => {
    if (user && tracker) {
      // Gebruik gehashte identifier in plaats van email voor privacy
      tracker.setUserID(user.id);
      tracker.setMetadata('role', role || 'user');
    }
  }, [user, role]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const showNavbar = isFullyAuthenticated && location.pathname !== '/reset-password';

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {showNavbar && <Navbar />}
        <Box component="main" sx={{ flexGrow: 1, pt: showNavbar ? 8 : 0 }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/login" replace />} />

              {/* Protected Routes */}
              <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/projects" element={<RequireAuth><Projects /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth><AdminPanel /></RequireAuth>} />
              <Route path="/employee-management" element={<RequireAuth><EmployeeManagement /></RequireAuth>} />
              <Route path="/project-management" element={<RequireAuth><ProjectManagement /></RequireAuth>} />
              <Route path="/projects/new" element={<RequireAuth><CreateProject /></RequireAuth>} />
              <Route path="/projects/:id" element={<RequireAuth><Projects /></RequireAuth>} />
              <Route path="/projects/:id/settings" element={<RequireAuth><ProjectSettings /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
              <Route path="/library" element={<RequireAuth><Library /></RequireAuth>} />
              <Route path="/stof-suggesties" element={<RequireAuth><StofSuggesties /></RequireAuth>} />
              <Route path="/metrics" element={<RequireAuth><Metrics /></RequireAuth>} />
              <Route path="/stoffenpakketten" element={<RequireAuth><Stoffenpakketten /></RequireAuth>} />

              {/* Fallback */}
              {!user && (
                <Route path="*" element={<Navigate to="/login" state={{ from: location }} replace />} />
              )}
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </ErrorBoundary>
  );
}

export default App;
